import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { DiscussionsComment } from "@air/component-discussions";
import { Close } from "@air/next-icons";
import { IconButton } from "@air/primitive-icon-button";
import { formatFullName, formatUpdatedAt, parseAirDateToISO } from "@air/utilities";
import { useCurrentUserSession } from "@air/utils-auth";
import { memo, useCallback, useMemo } from "react";
import { useSelector } from "react-redux";
import { AnnotationReplyButton } from "~/components/Annotations/AnnotationsAvatarsContainer/AnnotationAvatarContainer/components/AnnotationReplyButton";
import { useDiscussionPanelContextSelector } from "~/components/AssetModal/shared/context/DiscussionsPanelContext/DiscussionsPanelContext";
import { BOUNDING_BOX_AVATAR_SIZE } from "~/components/BoundingBox/components/BoundingBoxAvatar";
import { BOUNDING_BOX_TEXT_WIDTH } from "~/components/BoundingBox/constants";
import { CommentMenu } from "~/components/Discussions/Discussion/DiscussionComment/components/CommentMenu";
import { DiscussionCommentContent } from "~/components/Discussions/Discussion/DiscussionComment/components/DiscussionCommentContent";
import { ReopenDiscussionButton } from "~/components/Discussions/Discussion/DiscussionComment/components/ReopenDiscussionButton";
import { useCommentEditing } from "~/components/Discussions/Discussion/shared/useCommentEditing";
import { QueryParamNames } from "~/constants/search";
import { ANNOTATION_COMMENT_CONTAINER, CLOSE_FLOATING_COMMENT_BUTTON } from "~/constants/testIDs";
import { useURLBoardIdSelector } from "~/hooks/useURLBoardIdSelector";
import { useAccountContext } from "~/providers/AccountProvider";
import { centralizedBoardAncestorsSelector } from "~/store/centralizedBoard/selectors";
import { useDeleteCommentClick } from "~/swr-hooks/discussions/useDeleteCommentClick";
import { useGetDiscussionMentionsMembers } from "~/swr-hooks/discussions/useGetDiscussionMentionsMembers";
import { useResolveCommentClick } from "~/swr-hooks/discussions/useResolveCommentClick";
import { useSaveEditedCommentClick } from "~/swr-hooks/discussions/useSaveCommentEditClick";
import { isCommentAuthor } from "~/utils/comments";
import { setQueryParams } from "~/utils/PathUtils";
export const ANNOTATION_COMMENT_ID = "annotation-comment";
export const AnnotationComment = /*#__PURE__*/ memo((param)=>{
    let { discussion, position, canEdit, canResolve, canDelete, canReply, canShowMentionsButton } = param;
    const boardId = useURLBoardIdSelector();
    const ancestors = useSelector(centralizedBoardAncestorsSelector);
    const firstComment = discussion.comments.data[0];
    const { data: account } = useAccountContext();
    const { data: userSession } = useCurrentUserSession();
    const isAuthor = isCommentAuthor({
        comment: firstComment,
        account,
        userSession
    });
    const { enableEditing, isEditing, disableEditing } = useCommentEditing();
    const { handleResolveCommentClick } = useResolveCommentClick({
        trackLocation: "asset-page"
    });
    const { handleCommentDeleteClick } = useDeleteCommentClick({
        trackLocation: "asset-page"
    });
    const { handleSaveEditedCommentClick } = useSaveEditedCommentClick({
        trackLocation: "asset-page"
    });
    const isReplyingCurrentDiscussionIdSelector = useCallback((state)=>state.replyingDiscussionId === discussion.id, [
        discussion.id
    ]);
    const isAddingReply = useDiscussionPanelContextSelector(isReplyingCurrentDiscussionIdSelector);
    const mentionsList = useGetDiscussionMentionsMembers({
        boardId,
        ancestors,
        workspaceId: discussion.workspaceId
    });
    const positionValue = BOUNDING_BOX_AVATAR_SIZE + 12;
    const horizontalPos = useMemo(()=>position.includes("left") ? {
            right: positionValue
        } : {
            left: positionValue
        }, [
        position,
        positionValue
    ]);
    const onSaveClick = useCallback((newComment)=>{
        handleSaveEditedCommentClick({
            newComment,
            comment: firstComment,
            clipId: discussion.clipId,
            timestamp: discussion.timestamp,
            annotation: discussion.annotation
        });
    }, [
        handleSaveEditedCommentClick,
        firstComment,
        discussion
    ]);
    const commentMenu = useMemo(()=>discussion.resolved && canResolve ? /*#__PURE__*/ _jsx(ReopenDiscussionButton, {
            onToggleResolve: handleResolveCommentClick,
            discussion: discussion
        }) : /*#__PURE__*/ _jsxs("div", {
            className: "flex items-center",
            children: [
                /*#__PURE__*/ _jsx(CommentMenu, {
                    comment: firstComment,
                    discussion: discussion,
                    onToggleResolve: canResolve ? handleResolveCommentClick : undefined,
                    onDelete: canDelete && isAuthor ? handleCommentDeleteClick : undefined,
                    onEdit: canEdit && isAuthor ? enableEditing : undefined
                }),
                /*#__PURE__*/ _jsx(IconButton, {
                    "data-testid": CLOSE_FLOATING_COMMENT_BUTTON,
                    icon: Close,
                    appearance: "ghost",
                    color: "grey",
                    size: "small",
                    onClick: ()=>setQueryParams({
                            [QueryParamNames.discussionId]: null
                        }, "replace"),
                    label: "Close annotation"
                })
            ]
        }), [
        discussion,
        canResolve,
        handleResolveCommentClick,
        firstComment,
        canDelete,
        isAuthor,
        handleCommentDeleteClick,
        canEdit,
        enableEditing
    ]);
    if (!firstComment) return null;
    return /*#__PURE__*/ _jsx("div", {
        className: "rounded-lg bg-grey-1 shadow-popover",
        id: ANNOTATION_COMMENT_ID,
        "data-testid": ANNOTATION_COMMENT_CONTAINER,
        style: {
            ...horizontalPos,
            width: BOUNDING_BOX_TEXT_WIDTH
        },
        children: /*#__PURE__*/ _jsx(DiscussionsComment, {
            className: "px-4 py-3",
            actions: commentMenu,
            author: formatFullName(firstComment.account.firstName, firstComment.account.lastName),
            comment: /*#__PURE__*/ _jsx(DiscussionCommentContent, {
                isEditing: isEditing,
                cancelEditing: disableEditing,
                comment: firstComment,
                onSave: canEdit ? onSaveClick : undefined,
                mentionsList: mentionsList,
                shouldShowMentionsButton: canShowMentionsButton
            }),
            "data-testid": "ANNOTATION_COMMENT",
            timestamp: formatUpdatedAt(parseAirDateToISO(firstComment.createdAt).getTime()),
            secondaryActions: canReply && !discussion.resolved && !isAddingReply && /*#__PURE__*/ _jsx(AnnotationReplyButton, {
                discussion: discussion
            })
        })
    });
});
AnnotationComment.displayName = "AnnotationComment";
